import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSpinner,
  faChevronLeft,
  faChevronRight,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { Card, Table, InputGroup, Form, Button, Badge, Modal } from "@themesberg/react-bootstrap";

import {
  requestCourseOptOutOtp,
  userMyCourses,
  verifyCourseOptOutOtp,
} from "../../../pages/api";
import { Routes } from "../../../routes";
import { Link } from "react-router-dom";

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const getPurchaseCoursePath = () => {
  return Routes?.UserPurchaseCourseList?.path || "/user-purchase-course-list";
};

const getStatusBadgeStyle = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "pending":
      return { background: "#f59e0b", color: "#ffffff" };
    case "success":
      return { background: "#10b981", color: "#ffffff" };
    case "opted_out":
      return { background: "#f59e0b", color: "#ffffff" };
    case "failed":
      return { background: "#ef4444", color: "#ffffff" };
    default:
      return { background: "#6b7280", color: "#ffffff" };
  }
};

const mapCourseItem = (item) => ({
  id: item?.id ?? Date.now(),
  title: item?.title || "N/A",
  price: item?.price || "N/A",
  month_year: item?.month_year || "N/A",
  created_at: item?.created_at || "N/A",
  status: item?.status || "N/A",
  opt_out_status: Boolean(item?.opt_out_status),
  can_toggle: item?.can_toggle ?? false,
  is_final: item?.is_final ?? false,
  status_label: item?.status_label || "Enrolled",
  opt_out_deadline: item?.opt_out_deadline || null,
});

const formatStatus = (value) => {
  if (!value) return "N/A";
  const normalized = String(value).trim().toLowerCase();

  if (normalized === "opted_out") return "Opted Out";

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

const otpRegex = /^\d{6}$/;

export default function UserMyCourseList() {
  const [search, setSearch] = useState("");
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyPurchaseId, setBusyPurchaseId] = useState(null);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const searchDebounceRef = useRef(null);
  const firstSearchRenderRef = useRef(true);

  const fetchCourses = async (page = 1, perPage = 10, searchTerm = "") => {
    try {
      setLoading(true);

      const response = await userMyCourses({
        page,
        per_page: perPage,
        search: searchTerm,
      });

      const responseData = response?.data || {};
      const apiData = responseData?.data || {};
      const apiCourses = Array.isArray(apiData?.courses) ? apiData.courses : [];
      const apiPagination = apiData?.pagination || {};

      setCourses(apiCourses.map(mapCourseItem));
      setPagination({
        current_page: apiPagination.current_page ?? page,
        last_page: apiPagination.last_page ?? 1,
        per_page: apiPagination.per_page ?? perPage,
        total: apiPagination.total ?? apiCourses.length,
      });
    } catch (error) {
      console.error("Failed to fetch my courses:", error);
      toast.error(error?.response?.data?.message || "Unable to fetch my courses.");
      setCourses([]);
      setPagination({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses(1, pagination.per_page, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (firstSearchRenderRef.current) {
      firstSearchRenderRef.current = false;
      return;
    }

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      fetchCourses(1, pagination.per_page, search.trim());
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const filteredData = useMemo(() => courses, [courses]);

  const getShowingRange = () => {
    const total = pagination.total || 0;
    const current = pagination.current_page || 1;
    const perPage = pagination.per_page || 10;

    if (total === 0) {
      return { start: 0, end: 0 };
    }

    const start = (current - 1) * perPage + 1;
    const end = Math.min(current * perPage, total);

    return { start, end };
  };

  const getPageItems = () => {
    const totalPages = pagination.last_page || 1;
    const current = pagination.current_page || 1;

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (current <= 2) {
      return [1, 2, 3, "...", totalPages];
    }

    if (current >= totalPages - 1) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, "...", current - 1, current, current + 1, "...", totalPages];
  };

  const { start, end } = getShowingRange();

  const closeOtpModal = ({ revert = true } = {}) => {
    if (revert && pendingAction) {
      setCourses((prev) =>
        prev.map((course) =>
          course.id === pendingAction.coursePurchaseId
            ? { ...course, opt_out_status: pendingAction.previousStatus }
            : course
        )
      );
    }

    setOtpModalOpen(false);
    setOtpValue("");
    setOtpSubmitting(false);
    setBusyPurchaseId(null);
    setPendingAction(null);
  };

  const handleToggleChange = async (item, nextChecked) => {
    if (!item?.can_toggle || busyPurchaseId) return;

    const previousStatus = Boolean(item.opt_out_status);
    const action = nextChecked ? "opt_out" : "cancel_opt_out";

    // Optimistic UI only — backend status changes after OTP verify.
    setCourses((prev) =>
      prev.map((course) =>
        course.id === item.id ? { ...course, opt_out_status: nextChecked } : course
      )
    );

    setBusyPurchaseId(item.id);
    setPendingAction({
      coursePurchaseId: item.id,
      action,
      previousStatus,
      courseTitle: item.title,
    });

    try {
      await requestCourseOptOutOtp({
        course_purchase_id: item.id,
        action,
      });

      toast.success("OTP sent to your registered email.");
      setOtpValue("");
      setOtpModalOpen(true);
    } catch (error) {
      console.error("Opt-out OTP request failed:", error);
      const apiMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Unable to send OTP.";
      toast.error(apiMessage);
      setCourses((prev) =>
        prev.map((course) =>
          course.id === item.id ? { ...course, opt_out_status: previousStatus } : course
        )
      );
      setBusyPurchaseId(null);
      setPendingAction(null);
    }
  };

  const handleVerifyOtp = async () => {
    if (!pendingAction) return;

    const trimmedOtp = otpValue.trim();

    if (!otpRegex.test(trimmedOtp)) {
      toast.error("Please enter a valid 6-digit OTP.");
      return;
    }

    try {
      setOtpSubmitting(true);

      const response = await verifyCourseOptOutOtp({
        course_purchase_id: pendingAction.coursePurchaseId,
        action: pendingAction.action,
        otp: trimmedOtp,
      });

      const payload = response?.data || {};
      const data = payload?.data || {};

      setCourses((prev) =>
        prev.map((course) =>
          course.id === pendingAction.coursePurchaseId
            ? {
                ...course,
                opt_out_status: Boolean(
                  data.opt_out_status ?? pendingAction.action === "opt_out"
                ),
                can_toggle:
                  data.can_toggle !== undefined ? Boolean(data.can_toggle) : course.can_toggle,
                is_final: data.is_final !== undefined ? Boolean(data.is_final) : course.is_final,
                status_label: data.status_label || course.status_label,
              }
            : course
        )
      );

      toast.success(
        payload?.message ||
          (pendingAction.action === "opt_out"
            ? "Course opted out successfully."
            : "Opt-out cancelled successfully.")
      );

      setOtpModalOpen(false);
      setOtpValue("");
      setPendingAction(null);
      setBusyPurchaseId(null);

      fetchCourses(pagination.current_page, pagination.per_page, search.trim());
    } catch (error) {
      console.error("Opt-out OTP verify failed:", error);
      toast.error(error?.response?.data?.message || "OTP verification failed.");
      closeOtpModal({ revert: true });
    } finally {
      setOtpSubmitting(false);
    }
  };

  const getOptOutLabel = (item) => {
    if (item?.is_final || !item?.can_toggle) {
      return "Opt-out locked after enrollment deadline.";
    }

    if (item.opt_out_status) {
      return "Opt-out Status: Opted Out";
    }

    return "Opt-out Status: Not Opted Out";
  };

  const getOptOutHelper = (item) => {
    if (item?.is_final || !item?.can_toggle) {
      return "Final status is locked. Commission and credit note follow finalization rules.";
    }

    if (item.opt_out_status) {
      return "Turn off to cancel opt-out. OTP verification is required.";
    }

    return "Turn on to request opt-out. OTP verification is required.";
  };

  const otpModalTitle =
    pendingAction?.action === "cancel_opt_out"
      ? "Verify OTP to Cancel Opt-out"
      : "Verify OTP to Opt Out";

  const otpModalText =
    pendingAction?.action === "cancel_opt_out"
      ? "We have sent an OTP to your registered email. Enter the OTP to cancel your opt-out request."
      : "We have sent an OTP to your registered email. Enter the OTP to confirm opt-out.";

  return (
    <>
      <div className="lsg-page">
        <div className="course-header py-2 mb-3">
          <div className="course-header-title">
            <h2 className="lsg-page-title">My Courses</h2>
            <p className="lsg-page-subtitle">
              Track enrollment, payment, and opt-out status for each course.
            </p>
          </div>

          <div className="course-header-actions">
            <Button
              as={Link}
              to={getPurchaseCoursePath()}
              className="btn-lsg-primary"
              style={ACTIVE_BUTTON_STYLE}
            >
              <FontAwesomeIcon icon={faArrowRight} className="me-2" />
              Purchase Course
            </Button>
          </div>
        </div>

        <div className="lsg-info-box mb-4">
          You can opt out or cancel opt-out until the enrollment end date. OTP verification is
          required for every change. The final status after the deadline will decide commission and
          credit note eligibility as per system rules.
        </div>

        <Card className="border-0 shadow-sm rounded-4">
          <Card.Body className="p-4">
            <div className="course-list-top d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <div>
                <h5 className="mb-1">My Courses</h5>
              </div>

              <InputGroup className="course-search">
                <InputGroup.Text className="bg-white">
                  <FontAwesomeIcon icon={faSearch} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ boxShadow: "none" }}
                />
              </InputGroup>
            </div>

            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Sr No</th>
                    <th>Course Title</th>
                    <th>Price</th>
                    <th className="text-center">Date</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Opt-out</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-5">
                        <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                        Loading courses...
                      </td>
                    </tr>
                  ) : filteredData.length > 0 ? (
                    filteredData.map((item, index) => (
                      <tr key={item.id}>
                        <td className="fw-semibold">{start + index}</td>
                        <td>
                          <div className="fw-semibold">{item.title}</div>
                        </td>
                        <td className="fw-semibold">{formatCurrency(item.price)}</td>
                        <td className="text-center fw-semibold">{item.month_year}</td>
                        <td className="text-center">
                          <Badge
                            className="rounded-pill d-inline-flex align-items-center justify-content-center"
                            style={{
                              ...getStatusBadgeStyle(item.status),
                              minWidth: "5rem",
                              height: "2rem",
                              padding: "0 12px",
                            }}
                          >
                            {item.is_final
                              ? item.status_label
                              : item.opt_out_status
                                ? "Opted Out"
                                : formatStatus(item.status || "N/A")}
                          </Badge>
                        </td>
                        <td className="text-center">
                          <div className="opt-out-toggle-wrap">
                            <div className="fw-semibold small mb-1">{getOptOutLabel(item)}</div>
                            <Form.Check
                              type="switch"
                              id={`opt-out-switch-${item.id}`}
                              checked={Boolean(item.opt_out_status)}
                              disabled={!item.can_toggle || busyPurchaseId === item.id || otpModalOpen}
                              onChange={(e) => handleToggleChange(item, e.target.checked)}
                              label=""
                              className="d-inline-flex justify-content-center"
                            />
                            <div className="text-muted small mt-1">{getOptOutHelper(item)}</div>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-5">
                        No courses found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
              <div className="text-muted small">
                Showing {start} to {end} of {pagination.total} entries
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline-primary"
                  disabled={pagination.current_page <= 1 || loading}
                  onClick={() =>
                    fetchCourses(
                      Math.max(1, pagination.current_page - 1),
                      pagination.per_page,
                      search.trim()
                    )
                  }
                  className="course-page-btn"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </Button>

                {getPageItems().map((item, index) =>
                  item === "..." ? (
                    <span key={`dots-${index}`} className="text-muted px-1">
                      ...
                    </span>
                  ) : (
                    <Button
                      key={item}
                      type="button"
                      variant="outline-primary"
                      disabled={loading}
                      onClick={() => fetchCourses(item, pagination.per_page, search.trim())}
                      className={`course-page-btn ${
                        item === pagination.current_page ? "course-page-btn-active" : ""
                      }`}
                    >
                      {item}
                    </Button>
                  )
                )}

                <Button
                  type="button"
                  variant="outline-primary"
                  disabled={pagination.current_page >= pagination.last_page || loading}
                  onClick={() =>
                    fetchCourses(
                      Math.min(pagination.last_page, pagination.current_page + 1),
                      pagination.per_page,
                      search.trim()
                    )
                  }
                  className="course-page-btn"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        <Modal
          show={otpModalOpen}
          onHide={() => closeOtpModal({ revert: true })}
          centered
          backdrop="static"
        >
          <Modal.Header closeButton>
            <Modal.Title>{otpModalTitle}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <p className="mb-3">{otpModalText}</p>
            {pendingAction?.courseTitle ? (
              <p className="text-muted small mb-3">Course: {pendingAction.courseTitle}</p>
            ) : null}
            <Form.Group controlId="optOutOtpInput">
              <Form.Label>Enter OTP</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otpValue}
                onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-digit OTP"
                disabled={otpSubmitting}
              />
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button
              type="button"
              variant="outline-secondary"
              onClick={() => closeOtpModal({ revert: true })}
              disabled={otpSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              style={ACTIVE_BUTTON_STYLE}
              onClick={handleVerifyOtp}
              disabled={otpSubmitting || !otpRegex.test(otpValue.trim())}
            >
              {otpSubmitting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                  Verifying...
                </>
              ) : (
                "Verify OTP"
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        <style>{`
        .course-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 24px 0;
        }

        .course-header-title {
          flex: 0 0 auto;
        }

        .course-header-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
          flex: 1;
        }

        .course-search {
          width: 100%;
          max-width: 320px;
          flex: 0 1 360px;
        }

        .course-list-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .opt-out-toggle-wrap {
          min-width: 14rem;
          max-width: 18rem;
          margin: 0 auto;
        }

        .course-page-btn {
          width: 2rem;
          height: 2rem;
          padding: 0;
          border-radius: 0.5rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
        }

        .course-page-btn svg {
          font-size: 0.95rem;
        }

        @media (max-width: 767.98px) {
          .course-header {
            flex-direction: column;
            align-items: stretch;
          }

          .course-header-title {
            width: 100%;
          }

          .course-header-actions {
            width: 100%;
            flex-direction: column;
            align-items: stretch;
          }

          .course-search {
            width: 100%;
            max-width: none;
            flex: 1 1 auto;
          }

          .course-list-top {
            align-items: flex-start;
          }

          .table th,
          .table td {
            white-space: nowrap;
          }
        }
      `}</style>
      </div>
    </>
  );
}
